/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import {
  clearSummary,
  enableRegenerateSummary,
  generatePatientSummaryThunk,
  selectPatientSummaryState,
} from './patientSummarySlice';
import {
  ContextualizedWidgetReports,
  fetchPredefinedWidgetsThunk,
  selectDateRange,
  selectFhirCareEverywhere,
  selectFhirDiagnosticReports,
  selectPredefinedWidgetsMap,
} from '../../../Fhir/fhirSlice';
import { selectAllUserSelectionsFromAllMessages, selectDocumentVersionById } from '../../chatSlice';
import {
  applyRulesToFhirPatientData,
  DiagnosticReportAndObservationsBase,
  getOurObservationsIndexMap,
  getWidgetsFromDocumentVersion,
  sortFilterDiagnosticReportObservationsGrouped,
} from '../../../Fhir/fhir_utils';
import { RuleEngineData } from './ruleEngine/patientSummaryRuleEngine';
import { analyticsTrack } from '../../../analytics_utils';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import { renderComponentToString } from '../../../utils/commonFns';
import { SelectKeysSummaryTableInner } from '../InnerMessage/SelectKeysSummaryTable';

export function useGenerateSummary({ documentVersionId }: { documentVersionId?: string }) {
  const dispatch = useAppDispatch();
  const { summary, fetchingSummary, showRegenerateSummary } = useAppSelector(selectPatientSummaryState);

  // getting the algo keys from all messages to get all the selections in user flow
  const selectedAlgoKeys = useAppSelector((state) => selectAllUserSelectionsFromAllMessages(state));
  const selectedAlgoKeysRef = useRef(selectedAlgoKeys);

  const [contextualizedWidgetReports, setContextualizedWidgetReports] = useState({});
  const [isLoadingContextualizedWidgetReports, setIsLoadingContextualizedWidgetReports] = useState(false);

  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));

  const fhirPatientData = useAppSelector(selectFhirDiagnosticReports);
  const careEverywhereData = useAppSelector(selectFhirCareEverywhere);
  const predefinedWidgetOptionsMap = useAppSelector(selectPredefinedWidgetsMap);

  const dateRange = useAppSelector(selectDateRange);

  const historicalIndex = 0; // we need to use recent reports only for summary
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  // need to enable re-generate summary, whenever algo keys updated
  useEffect(() => {
    if (selectedAlgoKeysRef.current !== selectedAlgoKeys) {
      void dispatch(enableRegenerateSummary());
      selectedAlgoKeysRef.current = selectedAlgoKeys;
    }
  }, [dispatch, selectedAlgoKeys]);

  useEffect(() => {
    void dispatch(enableRegenerateSummary());
  }, [dispatch, dateRange]);

  useEffect(() => {
    if (!predefinedWidgetOptionsMap) {
      void dispatch(fetchPredefinedWidgetsThunk());
    }
  }, [dispatch, predefinedWidgetOptionsMap]);

  useEffect(() => {
    if (!predefinedWidgetOptionsMap) return undefined; // Not yet loaded

    setIsLoadingContextualizedWidgetReports(true);

    const widgets = getWidgetsFromDocumentVersion(documentVersion);

    let contextualizedWidgetsWithRecentReports: ContextualizedWidgetReports = {};

    widgets.forEach((fhir) => {
      fhir.predefinedWidgets.forEach((pw) => {
        let observations: DiagnosticReportAndObservationsBase[] | undefined = [];

        const predefinedWidgetId = pw.id;
        const predefinedWidget = predefinedWidgetOptionsMap![predefinedWidgetId!];
        if (!predefinedWidget || !predefinedWidget.rules) return undefined;
        observations = applyRulesToFhirPatientData(fhirPatientData, careEverywhereData, predefinedWidget?.rules ?? []);
        // get the custom widget configuration

        const { blacklist, whitelist } = predefinedWidget || {};

        // Get the index map for the whitelist and blacklist
        const whitelistIndexMap = getOurObservationsIndexMap(whitelist);
        const blacklistIndexMap = getOurObservationsIndexMap(blacklist);

        // Sort and filter the observations - The same FhirWidget logic is used here
        const panelObservationsSortedAndFiltered = sortFilterDiagnosticReportObservationsGrouped(
          observations,
          whitelistIndexMap,
          whitelist,
          blacklistIndexMap,
          blacklist,
          true,
          true
        );

        // If there are no observations, then still store with widget but with blank reports and skip to the next widget
        if (!panelObservationsSortedAndFiltered || panelObservationsSortedAndFiltered.length === 0) {
          if (predefinedWidgetId && predefinedWidget) {
            contextualizedWidgetsWithRecentReports[predefinedWidgetId] = {
              widget: predefinedWidget,
              reports: [],
            };
          }
          return; // widget doesn't have any observations skip to the next widget
        }

        let newHistoricalIndex = historicalIndex;

        panelObservationsSortedAndFiltered?.forEach((panelWithObservations, index) => {
          // As implemented in FhirWidget, we need to skip the panels that are hidden from the date selector
          const maxHistoricalIndex = panelWithObservations.length - 1;
          newHistoricalIndex = Math.max(0, Math.min(newHistoricalIndex, maxHistoricalIndex));
          while (
            panelWithObservations[newHistoricalIndex]?.isLoaded &&
            panelWithObservations[newHistoricalIndex]?.hidePanelFromDateSelector
          ) {
            newHistoricalIndex += 1;
          }
          newHistoricalIndex = Math.max(0, Math.min(newHistoricalIndex, maxHistoricalIndex));

          const panelObs = panelWithObservations[newHistoricalIndex];

          let hiddenObservationList = panelObs?.hiddenObservationList;
          let observationList = panelObs?.observationList;
          if (observationList && observationList.length === 0 && !panelObs?.hideAllIfNoWhitelistMatches) {
            // If observationList is empty, then use hiddenObservationList and so just View everything
            observationList = hiddenObservationList;
            hiddenObservationList = [];
          }
          if (predefinedWidgetId) {
            // store the widget with the recent reports
            contextualizedWidgetsWithRecentReports[predefinedWidgetId] = {
              widget: predefinedWidget,
              reports: [panelWithObservations[newHistoricalIndex]!],
            };
          }
        });
        setContextualizedWidgetReports(contextualizedWidgetsWithRecentReports);
      });
    });

    setIsLoadingContextualizedWidgetReports(false);
  }, [
    fhirPatientData,
    predefinedWidgetOptionsMap,
    documentVersion,
    setContextualizedWidgetReports,
    careEverywhereData,
  ]);

  const getSummary = async () => {
    void dispatch(clearSummary());
    const data = { contextualizedWidgetReports, dateRange } as RuleEngineData;
    const algoSelectionHTML = !!selectedAlgoKeys
      ? renderComponentToString(
          <SelectKeysSummaryTableInner
            selectedKeys={selectedAlgoKeys}
            className="algo_selection_summary"
            pearlUserInfo={pearlUserInfo}
          />
        )
      : '';
    await dispatch(generatePatientSummaryThunk({ data, documentVersion, algoSelectionHTML }));
    return;
  };

  const summaryButtonHandler = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getSummary();
    analyticsTrack(
      'summary_action',
      {
        action: showRegenerateSummary ? 'summary_regenerated' : 'summary_generated',
        documentName: documentVersion?.title,
        documentId: documentVersion?.document.id,
        documentVersionId: documentVersion?.id,
        documentSlug: documentVersion?.document?.slug,
        documentType: documentVersion?.document?.docType,
      },
      pearlUserInfo
    );
  };

  const isDisabled = (summary && !fetchingSummary && !showRegenerateSummary) || isLoadingContextualizedWidgetReports;

  return {
    isFetchingSummary: fetchingSummary,
    isDisabled,
    summaryButtonHandler,
    showRegenerateSummary,
  };
}
